import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { specialAssignmentsAPI } from '../../utils/api';
import { decimalToDMS } from '../../utils/gpsUtils';
import notificationService from '../../services/notificationService';
import '../personnel/SpecialAssignment.css';

const AdminSpecialAssignments = () => {
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'assign'
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterName, setFilterName] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [exportingPDF, setExportingPDF] = useState(false);
  
  // Assignment form state
  const [assignmentForm, setAssignmentForm] = useState({
    personnelId: null,
    title: '',
    description: '',
    location: '',
    date: ''
  });
  const [personnelOptions, setPersonnelOptions] = useState([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);

  useEffect(() => {
    if (activeTab === 'all') {
      loadAssignments();
      loadStats();
    }
  }, [activeTab, filterStatus, filterName, filterDateFrom, filterDateTo]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterName.trim()) filters.name = filterName.trim();
      if (filterDateFrom) filters.dateFrom = filterDateFrom;
      if (filterDateTo) filters.dateTo = filterDateTo;
      
      const response = await specialAssignmentsAPI.getAll(filters);
      setAssignments(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Error loading assignments:', error);
      showAlert('error', 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await specialAssignmentsAPI.getStats();
      setStats(response.data?.data || response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Debounced personnel search
  const searchPersonnel = useCallback(
    debounce(async (inputValue) => {
      if (!inputValue || inputValue.length < 2) {
        setPersonnelOptions([]);
        return;
      }

      setLoadingPersonnel(true);
      try {
        const response = await specialAssignmentsAPI.searchPersonnel(inputValue);
        const personnel = response.data?.data || response.data || [];
        
        const options = personnel.map(p => ({
          value: p.id,
          label: `${p.firstName} ${p.lastName} (${p.personnelId || p.email})`,
          data: p
        }));
        
        setPersonnelOptions(options);
      } catch (error) {
        console.error('Error searching personnel:', error);
        showAlert('error', 'Failed to search personnel');
      } finally {
        setLoadingPersonnel(false);
      }
    }, 500),
    []
  );

  const handlePersonnelInputChange = (inputValue) => {
    searchPersonnel(inputValue);
  };

  const handlePersonnelChange = (selectedOption) => {
    setAssignmentForm(prev => ({
      ...prev,
      personnelId: selectedOption ? selectedOption.value : null
    }));
  };

  const handleAssignmentFormChange = (e) => {
    const { name, value } = e.target;
    setAssignmentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!assignmentForm.personnelId) {
      showAlert('error', 'Please select a personnel');
      return;
    }
    if (!assignmentForm.title.trim()) {
      showAlert('error', 'Title is required');
      return;
    }
    if (!assignmentForm.description.trim()) {
      showAlert('error', 'Description is required');
      return;
    }
    if (!assignmentForm.location.trim()) {
      showAlert('error', 'Location is required');
      return;
    }
    if (!assignmentForm.date) {
      showAlert('error', 'Date is required');
      return;
    }

    setSubmittingAssignment(true);
    try {
      // Debug: Log what we're sending
      console.log('📤 Sending assignment data:', assignmentForm);
      console.log('📤 Personnel ID type:', typeof assignmentForm.personnelId, assignmentForm.personnelId);
      
      await specialAssignmentsAPI.assignToPersonnel(assignmentForm);

      // Trigger notification to other admins and sub-admins
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const selectedPersonnel = personnelOptions.find(p => p.value === assignmentForm.personnelId);
      
      notificationService.triggerAssignmentAction('assignment-assigned', {
        adminName: currentUser?.name || currentUser?.username || 'Admin',
        adminId: currentUser?.id || currentUser?.userId,
        personnelName: selectedPersonnel?.label || 'Personnel',
        personnelId: assignmentForm.personnelId,
        assignmentTitle: assignmentForm.title,
        location: assignmentForm.location,
        date: assignmentForm.date
      });

      showAlert('success', 'Assignment successfully assigned to personnel!');
      
      // Reset form
      setAssignmentForm({
        personnelId: null,
        title: '',
        description: '',
        location: '',
        date: ''
      });
      setPersonnelOptions([]);
    } catch (error) {
      console.error('Error assigning assignment:', error);
      console.error('Error details:', error.response?.data);
      console.error('Status code:', error.response?.status);
      showAlert('error', error.response?.data?.message || 'Failed to assign assignment');
    } finally {
      setSubmittingAssignment(false);
    }
  };

  const handleApprove = async (id) => {
    const notes = prompt('Add approval notes (optional):');
    
    try {
      // Get assignment details before approving
      const assignment = assignments.find(a => a.id === id);
      
      await specialAssignmentsAPI.approveSubmission(id, notes || '');
      showAlert('success', 'Assignment approved successfully');
      
      // Trigger notification to other admins and sub-admins
      if (assignment) {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        notificationService.triggerAssignmentAction('assignment-approved', {
          adminName: currentUser?.name || currentUser?.username || 'Admin',
          adminId: currentUser?.id || currentUser?.userId,
          assignmentTitle: assignment.title,
          personnelName: assignment.Personnel?.name || 'Personnel',
        });
      }
      
      loadAssignments();
      loadStats();
    } catch (error) {
      console.error('Error approving assignment:', error);
      showAlert('error', 'Failed to approve assignment');
    }
  };

  const handleReject = async (id) => {
    const notes = prompt('Please provide a reason for rejection:');
    if (!notes) {
      showAlert('error', 'Rejection reason is required');
      return;
    }

    try {
      await specialAssignmentsAPI.rejectSubmission(id, notes);
      showAlert('success', 'Assignment rejected');
      loadAssignments();
      loadStats();
    } catch (error) {
      console.error('Error rejecting assignment:', error);
      showAlert('error', 'Failed to reject assignment');
    }
  };

  const handleVerifyCompletion = async (id) => {
    const confirm = window.confirm('Are you sure you want to verify and close this assignment?');
    if (!confirm) return;

    const notes = prompt('Add verification notes (optional):');

    try {
      await specialAssignmentsAPI.verifyCompletion(id, notes || '');
      showAlert('success', 'Assignment verified and closed successfully');
      loadAssignments();
      loadStats();
    } catch (error) {
      console.error('Error verifying assignment:', error);
      showAlert('error', 'Failed to verify assignment');
    }
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.');
    if (!confirm) return;

    try {
      // Get assignment details before deleting
      const assignment = assignments.find(a => a.id === id);
      
      await specialAssignmentsAPI.delete(id);

      // Trigger notification to other admins and sub-admins
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const personnelName = assignment?.personnel?.name || 
                            assignment?.Personnel?.name || 
                            assignment?.personnelName || 
                            'Personnel';
      
      notificationService.triggerAssignmentAction('assignment-deleted', {
        adminName: currentUser?.name || currentUser?.username || 'Admin',
        adminId: currentUser?.id || currentUser?.userId,
        assignmentTitle: assignment?.title || 'Special Assignment',
        personnelName: personnelName,
        location: assignment?.location
      });

      showAlert('success', 'Assignment deleted successfully');
      loadAssignments();
      loadStats();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      showAlert('error', 'Failed to delete assignment');
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const viewDetails = (assignment) => {
    console.log('📄 Assignment details:', assignment);
    console.log('👤 Personnel data:', assignment.personnel);
    setSelectedAssignment(assignment);
    setShowDetailModal(true);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'pending': 'pending',
      'approved': 'approved',
      'rejected': 'rejected',
      'in_progress': 'in-progress',
      'completed': 'completed',
      'closed': 'closed'
    };
    return statusMap[status] || 'pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'closed': 'Closed'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const filters = {};
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterName.trim()) filters.name = filterName.trim();
      if (filterDateFrom) filters.dateFrom = filterDateFrom;
      if (filterDateTo) filters.dateTo = filterDateTo;
      
      const response = await specialAssignmentsAPI.exportPDF(filters);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `special-assignments-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showAlert('success', 'PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showAlert('error', error.response?.data?.message || 'Failed to export PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '42px',
      borderColor: '#ddd',
      '&:hover': {
        borderColor: '#4285f4'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#4285f4' : state.isFocused ? '#e8f0fe' : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer'
    })
  };

  return (
    <div className="special-assignment-container">
      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'danger' : alert.type}`}>
          <i className={`fas fa-${alert.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {alert.message}
        </div>
      )}

      <div className="assignment-card">
        <div className="assignment-header">
          <h1 className="assignment-title">Special Assignments Management</h1>
        </div>

        {/* Stats Cards */}
        {stats && activeTab === 'all' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: '#fff3cd', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>{stats.pending || 0}</div>
              <div style={{ fontSize: '14px', color: '#856404' }}>Pending Review</div>
            </div>
            <div style={{ background: '#d1ecf1', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0c5460' }}>{stats.inProgress || 0}</div>
              <div style={{ fontSize: '14px', color: '#0c5460' }}>In Progress</div>
            </div>
            <div style={{ background: '#cce5ff', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#004085' }}>{stats.completed || 0}</div>
              <div style={{ fontSize: '14px', color: '#004085' }}>Completed</div>
            </div>
            <div style={{ background: '#e2e3e5', padding: '16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#383d41' }}>{stats.closed || 0}</div>
              <div style={{ fontSize: '14px', color: '#383d41' }}>Closed</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs-nav">
            <button
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <i className="fas fa-list"></i> All Assignments
            </button>
            <button
              className={`tab-button ${activeTab === 'assign' ? 'active' : ''}`}
              onClick={() => setActiveTab('assign')}
            >
              <i className="fas fa-user-plus"></i> Assign to Personnel
            </button>
          </div>
        </div>

        {/* All Assignments View */}
        {activeTab === 'all' && (
          <>
            {/* Filters Section */}
            <div className="filters-container">
              <div className="filters-header">
                <i className="fas fa-filter"></i>
                <span>Filters</span>
              </div>
              <div className="filters-row">
                <div className="filter-group">
                  <label htmlFor="filter-status">
                    <i className="fas fa-tag"></i> Status
                  </label>
                  <select
                    id="filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="filter-name">
                    <i className="fas fa-user"></i> Personnel Name
                  </label>
                  <input
                    id="filter-name"
                    type="text"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder="Search by name..."
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="filter-date-from">
                    <i className="fas fa-calendar-alt"></i> Date From
                  </label>
                  <input
                    id="filter-date-from"
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="filter-date-to">
                    <i className="fas fa-calendar-alt"></i> Date To
                  </label>
                  <input
                    id="filter-date-to"
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="export-pdf">
                    <i className="fas fa-file-pdf"></i> Export
                  </label>
                  <button
                    id="export-pdf"
                    className="btn btn-primary"
                    onClick={handleExportPDF}
                    disabled={exportingPDF || loading}
                    title="Export to PDF"
                  >
                    {exportingPDF ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Exporting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-pdf"></i> Export PDF
                      </>
                    )}
                  </button>
                </div>

                {(filterStatus !== 'all' || filterName || filterDateFrom || filterDateTo) && (
                  <div className="filter-group">
                    <label style={{ opacity: 0 }}>Clear</label>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setFilterStatus('all');
                        setFilterName('');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                      }}
                      title="Clear all filters"
                    >
                      <i className="fas fa-times"></i> Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="assignment-list">
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading assignments...</p>
                </div>
              ) : assignments.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <i className="fas fa-inbox"></i>
                  </div>
                  <p className="empty-state-text">No assignments found</p>
                  <p className="empty-state-subtext">No special assignments match the current filter</p>
                </div>
              ) : (
                assignments.map((assignment) => (
                  <div key={assignment.id} className="assignment-item">
                    <div className="assignment-item-header">
                      <div>
                        <h3 className="assignment-item-title">{assignment.title}</h3>
                        <span className={`status-badge ${getStatusBadgeClass(assignment.status)}`}>
                          {getStatusLabel(assignment.status)}
                        </span>
                      </div>
                    </div>
                    <div className="assignment-item-meta">
                      <span>
                        <i className="fas fa-user"></i>{' '}
                        {assignment.personnel?.name || 
                         `${assignment.personnel?.firstName || ''} ${assignment.personnel?.lastName || ''}`.trim() ||
                         'N/A'}
                      </span>
                      {assignment.siteName && (
                        <span><i className="fas fa-building"></i> {assignment.siteName}</span>
                      )}
                      <span><i className="fas fa-map-marker-alt"></i> {assignment.location}</span>
                      <span><i className="fas fa-clock"></i> Submitted: {formatDateTime(assignment.createdAt || assignment.created_at)}</span>
                    </div>
                    <p className="assignment-item-description">
                      {assignment.description.substring(0, 150)}
                      {assignment.description.length > 150 ? '...' : ''}
                    </p>
                    <div className="assignment-item-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => viewDetails(assignment)}
                      >
                        <i className="fas fa-eye"></i> View Details
                      </button>
                      {assignment.status === 'pending' && (assignment.createdBy === 'self' || assignment.created_by === 'self') && (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleApprove(assignment.id)}
                          >
                            <i className="fas fa-check"></i> Approve
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleReject(assignment.id)}
                          >
                            <i className="fas fa-times"></i> Reject
                          </button>
                        </>
                      )}
                      {assignment.status === 'pending' && (assignment.createdBy === 'admin' || assignment.created_by === 'admin') && (
                        <span className="waiting-badge">
                          <i className="fas fa-hourglass-half"></i> Waiting for Personnel Response
                        </span>
                      )}
                      {assignment.status === 'completed' && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleVerifyCompletion(assignment.id)}
                        >
                          <i className="fas fa-check-circle"></i> Verify & Close
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(assignment.id)}
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Assign to Personnel Form */}
        {activeTab === 'assign' && (
          <form onSubmit={handleAssignmentSubmit}>
            <div className="form-section">
              <h2 className="form-section-title">Assign Special Assignment to Personnel</h2>

              <div className="form-group">
                <label htmlFor="personnel">
                  Personnel <span className="required">*</span>
                </label>
                <Select
                  id="personnel"
                  options={personnelOptions}
                  onInputChange={handlePersonnelInputChange}
                  onChange={handlePersonnelChange}
                  isLoading={loadingPersonnel}
                  placeholder="Type to search personnel..."
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.length < 2
                      ? 'Type at least 2 characters to search'
                      : 'No personnel found'
                  }
                  styles={customSelectStyles}
                  isClearable
                  isDisabled={submittingAssignment}
                />
              </div>

              <div className="form-group">
                <label htmlFor="title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={assignmentForm.title}
                  onChange={handleAssignmentFormChange}
                  placeholder="Enter assignment title"
                  disabled={submittingAssignment}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">
                  Description <span className="required">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={assignmentForm.description}
                  onChange={handleAssignmentFormChange}
                  placeholder="Describe the assignment..."
                  disabled={submittingAssignment}
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">
                  Location <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  className="form-control"
                  value={assignmentForm.location}
                  onChange={handleAssignmentFormChange}
                  placeholder="Enter location"
                  disabled={submittingAssignment}
                />
              </div>

              <div className="form-group">
                <label htmlFor="date">
                  Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  className="form-control"
                  value={assignmentForm.date}
                  onChange={handleAssignmentFormChange}
                  disabled={submittingAssignment}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setAssignmentForm({
                    personnelId: null,
                    title: '',
                    description: '',
                    location: '',
                    date: ''
                  });
                  setPersonnelOptions([]);
                }}
                disabled={submittingAssignment}
              >
                <i className="fas fa-undo"></i> Clear
              </button>
              <button type="submit" className="btn btn-primary" disabled={submittingAssignment}>
                {submittingAssignment ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Assigning...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i> Assign to Personnel
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAssignment && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="modal-title">{selectedAssignment.title}</h2>
                <span className={`status-badge ${getStatusBadgeClass(selectedAssignment.status)}`}>
                  {getStatusLabel(selectedAssignment.status)}
                </span>
              </div>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Personnel Info Card */}
              {(selectedAssignment.personnel || selectedAssignment.Personnel) && (
                <div className="personnel-card">
                  <div className="personnel-card-header">
                    <i className="fas fa-user-circle"></i>
                    <span>Assigned Personnel</span>
                  </div>
                  <div className="personnel-card-body">
                    <div className="personnel-info">
                      <div className="personnel-name">
                        {selectedAssignment.personnel?.name || 
                         `${selectedAssignment.personnel?.firstName || selectedAssignment.personnel?.first_name || ''} ${selectedAssignment.personnel?.lastName || selectedAssignment.personnel?.last_name || ''}`.trim() ||
                         selectedAssignment.Personnel?.name || 
                         `${selectedAssignment.Personnel?.firstName || ''} ${selectedAssignment.Personnel?.lastName || ''}`.trim() ||
                         'N/A'}
                      </div>
                      {(selectedAssignment.personnel?.position || selectedAssignment.Personnel?.position) && (
                        <div className="personnel-position">
                          <i className="fas fa-briefcase"></i> {selectedAssignment.personnel?.position || selectedAssignment.Personnel?.position}
                        </div>
                      )}
                      {(selectedAssignment.personnel?.personnelId || selectedAssignment.personnel?.personnel_id || selectedAssignment.Personnel?.personnelId || selectedAssignment.Personnel?.personnel_id) && (
                        <div className="personnel-id">
                          ID: {selectedAssignment.personnel?.personnelId || selectedAssignment.personnel?.personnel_id || selectedAssignment.Personnel?.personnelId || selectedAssignment.Personnel?.personnel_id}
                        </div>
                      )}
                      {(selectedAssignment.personnel?.email || selectedAssignment.Personnel?.email) && (
                        <div className="personnel-email">
                          <i className="fas fa-envelope"></i> {selectedAssignment.personnel?.email || selectedAssignment.Personnel?.email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Description Section */}
              <div className="detail-section">
                <div className="detail-section-label">
                  <i className="fas fa-align-left"></i> Description
                </div>
                <div className="detail-section-content">{selectedAssignment.description}</div>
              </div>

              {/* Assignment Details Grid */}
              <div className="detail-grid">
                {selectedAssignment.siteName && (
                  <div className="detail-card">
                    <div className="detail-card-content">
                      <div className="detail-card-label"><i className="fas fa-building"></i> Site Name</div>
                      <div className="detail-card-value">{selectedAssignment.siteName}</div>
                    </div>
                  </div>
                )}

                <div className="detail-card">
                  <div className="detail-card-content">
                    <div className="detail-card-label"><i className="fas fa-map-marker-alt"></i> Location</div>
                    <div className="detail-card-value">{selectedAssignment.location}</div>
                  </div>
                </div>

                {/* GPS Coordinates Section */}
                {(selectedAssignment.latitude && selectedAssignment.longitude) && (
                  <div className="detail-card">
                    <div className="detail-card-content">
                      <div className="detail-card-label"><i className="fas fa-crosshairs"></i> GPS Coordinates</div>
                      <div className="detail-card-value">
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                          {decimalToDMS(parseFloat(selectedAssignment.latitude), true)} {decimalToDMS(parseFloat(selectedAssignment.longitude), false)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                          {parseFloat(selectedAssignment.latitude).toFixed(6)}, {parseFloat(selectedAssignment.longitude).toFixed(6)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="detail-card">
                  <div className="detail-card-content">
                    <div className="detail-card-label"><i className="fas fa-clock"></i> Submitted On</div>
                    <div className="detail-card-value">{formatDateTime(selectedAssignment.createdAt || selectedAssignment.created_at)}</div>
                  </div>
                </div>

                {(selectedAssignment.updatedAt || selectedAssignment.updated_at) && (
                  <div className="detail-card">
                    <div className="detail-card-content">
                      <div className="detail-card-label"><i className="fas fa-sync-alt"></i> Last Updated</div>
                      <div className="detail-card-value">{formatDate(selectedAssignment.updatedAt || selectedAssignment.updated_at)}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes Section */}
              {selectedAssignment.notes && (
                <div className="detail-section">
                  <div className="detail-section-label">
                    <i className="fas fa-sticky-note"></i> Notes
                  </div>
                  <div className="detail-section-content">{selectedAssignment.notes}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedAssignment.status === 'pending' && (selectedAssignment.createdBy === 'self' || selectedAssignment.created_by === 'self') && (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      setShowDetailModal(false);
                      handleApprove(selectedAssignment.id);
                    }}
                  >
                    <i className="fas fa-check"></i> Approve
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => {
                      setShowDetailModal(false);
                      handleReject(selectedAssignment.id);
                    }}
                  >
                    <i className="fas fa-times"></i> Reject
                  </button>
                </>
              )}
              {selectedAssignment.status === 'pending' && (selectedAssignment.createdBy === 'admin' || selectedAssignment.created_by === 'admin') && (
                <div className="waiting-info">
                  <i className="fas fa-info-circle"></i>
                  <span>Waiting for personnel to accept or reject this assignment</span>
                </div>
              )}
              {selectedAssignment.status === 'completed' && (
                <button 
                  className="btn btn-success" 
                  onClick={() => {
                    setShowDetailModal(false);
                    handleVerifyCompletion(selectedAssignment.id);
                  }}
                >
                  <i className="fas fa-check-circle"></i> Verify & Close
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default AdminSpecialAssignments;
